import React, { memo, useMemo } from 'react';

const Table = memo(function Table({ headers, data, renderRow, className = '' }) {
  // Memoize the empty state to prevent unnecessary re-renders
  const emptyState = useMemo(() => (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow-lg p-8 text-center border border-indigo-200">
      <div className="flex flex-col items-center space-y-3">
        <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-blue-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-700">No data available</p>
          <p className="text-sm mt-1 text-gray-500">Try adjusting your filters or adding new content</p>
        </div>
      </div>
    </div>
  ), []);

  // Memoize container styles
  const containerStyles = useMemo(() => 
    "overflow-hidden rounded-xl shadow-xl border border-gray-200/50 bg-white transform-gpu", 
    []
  );

  const tableStyles = useMemo(() => 
    `min-w-full divide-y divide-gray-200 ${className}`, 
    [className]
  );

  // Memoize header cells
  const headerCells = useMemo(() => 
    headers.map((header, index) => (
      <th
        key={index}
        scope="col"
        className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200"
      >
        {header}
      </th>
    )), 
    [headers]
  );

  // Memoize table rows for performance
  const tableRows = useMemo(() => {
    if (!data || data.length === 0) return null;
    
    return data.map((item, index) => {
      const row = renderRow(item, index);
      return React.cloneElement(row, {
        key: row.key || index,
        className: `hover:bg-gradient-to-r hover:from-indigo-50 hover:to-blue-50 transition-all duration-200 ease-in-out transform-gpu ${row.props.className || ''}`
      });
    });
  }, [data, renderRow]);

  if (!data || data.length === 0) {
    return emptyState;
  }

  return (
    <div className={containerStyles}>
      <div className="overflow-x-auto scroll-smooth">
        <table className={tableStyles}>
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 will-change-scroll">
            <tr>
              {headerCells}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100 will-change-scroll">
            {tableRows}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default Table;